import { ProjectId, User as UserT, UserId } from 'api/models';
import { Project as ProjectT, ProjectPhase } from 'api/project';
import {
  TimesheetAdhocTask,
  TimesheetCalendarTask,
  TimesheetTask,
} from 'components/organisms/timesheets/types';
import { TimesheetCategory } from '..';
import { CategoryId } from '../categories';

export enum TimesheetReportType {
  Project = 'project',
  User = 'user',
  Category = 'Category',
  Task = 'task',
  Phase = 'phase',
}
export type ProjectWiseGroupBy =
  | TimesheetReportType.User
  | TimesheetReportType.Category;
export type UsertWiseGroupBy =
  | TimesheetReportType.Project
  | TimesheetReportType.Category;
export type CategoryWiseGroupBy =
  | TimesheetReportType.User
  | TimesheetReportType.Project;

export enum CategorySummaryType {
  Top = 'top',
  Other = 'other',
  None = 'none',
}
export interface CategorySummary {
  type: CategorySummaryType.Top;
  categoryId: number;
  totalMinutes: number;
}
export interface OtherCategorySummary {
  type: CategorySummaryType.Other;
  totalMinutes: number;
}
export interface NonCategorySummary {
  type: CategorySummaryType.None;
  totalMinutes: number;
}

export type TimesheetReportCategorySummary =
  | CategorySummary
  | OtherCategorySummary
  | NonCategorySummary;

export interface TimesheetReportSummary {
  billableMinutes: number;
  nonBillableMinutes: number;
  capacityInMinutes: number;
  numberOfUniqueUser?: number;
  categories: TimesheetReportCategorySummary[];
  users?: UserT[];
  totalAllocatedMinutes?: number;
}
export type TimesheetProjectWiseReportSummary = TimesheetReportSummary;

export type TimesheetUserWiseReportSummary = TimesheetReportSummary;

export type TimesheetCategoryWiseReportSummary = Omit<
  TimesheetReportSummary,
  'capacityInMinutes' | 'categories'
>;

export interface TimesheetReportCategoryData {
  categoryId?: number;
  totalMinutes: number;
}
export interface TimesheetProjectReport {
  type: TimesheetReportType.Project;
  project?: ProjectT;
  budgetedHours?: number;
  billableMinutes: number;
  nonBillableMinutes: number;
  categories: TimesheetReportCategoryData[];
  allocatedMinutes: number;
}

export interface TimesheetUserReport {
  type: TimesheetReportType.User;
  user: UserT;
  billableMinutes: number;
  nonBillableMinutes: number;
  categories: TimesheetReportCategoryData[];
  allocatedMinutes: number;
  timeOffMinutes: number;
}

export interface TimesheetTaskReport {
  type: TimesheetReportType.Task;
  task: TimesheetTask | TimesheetAdhocTask | TimesheetCalendarTask;
  billableMinutes: number;
  nonBillableMinutes: number;
  categories: TimesheetReportCategoryData[];
}
export interface TimesheetPhaseReport {
  type: TimesheetReportType.Phase;
  projectPhase: Omit<ProjectPhase, 'progressStatus'>;
  billableMinutes: number;
  nonBillableMinutes: number;
  categories: TimesheetReportCategoryData[];
}
export interface TimesheetCategoryReport {
  type: TimesheetReportType.Category;
  category?: TimesheetCategory;
  billableMinutes: number;
  nonBillableMinutes: number;
}

export type TimesheetReport =
  | TimesheetProjectReport
  | TimesheetCategoryReport
  | TimesheetPhaseReport
  | TimesheetUserReport
  | TimesheetTaskReport;

export interface TimesheetReportsSummary {
  billableMinutes: number;
  nonBillableMinutes: number;
  budgetedHours?: number;
  categories?: TimesheetReportCategoryData[];
  effort?: number;
  allocatedMinutes: number;
  timeOffMinutes: number;
}

export interface TimesheetReportExport {
  exportsMetaDataId: number;
  status: string;
  downloadUrl: string;
  requestedBy: {
    userId: number;
    identityType: string;
  };
  requestedAt: number;
  createdAt: number;
}

type NonProject = ['project_id', 'non-project'];

type NoCategory = ['category_id', 'no-category'];

type TimeEntryCategoryId = ['category_id', CategoryId];

export type ProjectParam = ProjectId | NonProject;
export type CategoryParam = TimeEntryCategoryId | NoCategory;
export type UserParam = UserId;

export const NonProject = (): NonProject => ['project_id', 'non-project'];
export const NoCategory = (): NoCategory => ['category_id', 'no-category'];
export const TimeEntryCategoryId = (id: number): TimeEntryCategoryId => [
  'category_id',
  id as CategoryId,
];
